import React, {
  useContext,
  useEffect,
  useState,
  createContext,
  FC,
  useMemo,
  PropsWithChildren,
} from 'react';
import { isEqual } from 'lodash';

interface Dimensions {
  width: number;
  height: number;
}

export const UseElementSizeTestContext = createContext<Dimensions | null>(null);

export const UseElementSizeTestProvider: FC<PropsWithChildren<Dimensions>> = ({
  width,
  height,
  children,
}) => {
  const state = useMemo(
    () => ({
      width,
      height,
    }),
    [height, width],
  );

  return (
    <UseElementSizeTestContext.Provider value={state}>
      {children}
    </UseElementSizeTestContext.Provider>
  );
};

/**
 * Create an empty element ref and keep track of the size of a element
 * when it is assigned to a DOM element. The size of the element is
 * updated when the window is resized.
 *
 * @returns {{elementSize: ElementSize, ref: React.RefCallback<HTMLElement>}}
 * A ref callback to be set to a React element and the dimensions of that element
 *
 */
export const useElementSize = (): {
  elementSize: Dimensions | null;
  ref: React.RefCallback<HTMLElement>;
} => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [elementSize, setElementSize] = useState<Dimensions | null>(null);
  const testDimensions = useContext(UseElementSizeTestContext);

  useEffect(() => {
    if (!element || 'ResizeObserver' in window === false || testDimensions) return;
    let frameID: number;
    const anchorObserver = new ResizeObserver(entries => {
      frameID = window.requestAnimationFrame(() => {
        for (const entry of entries) {
          // if the children of the anchor change, potentially update `anchorSize`
          setElementSize(elementSize => {
            let width = 0,
              height = 0;
            if (entry.borderBoxSize?.[0]) {
              const { blockSize, inlineSize } = entry.borderBoxSize[0];
              width = inlineSize;
              height = blockSize;
            } else {
              const { width: rectWidth, height: rectHeight } = element.getBoundingClientRect();
              width = rectWidth;
              height = rectHeight;
            }
            const updatedElementSize = { width, height };
            // if sizes are equal, return current `elementSize` object to prevent state update
            return isEqual(elementSize, updatedElementSize) ? elementSize : updatedElementSize;
          });
        }
      });
    });
    anchorObserver.observe(element);
    return () => {
      anchorObserver.disconnect();
      window.cancelAnimationFrame(frameID);
    };
  }, [element, testDimensions]);

  return { elementSize: testDimensions || elementSize, ref: setElement };
};
